import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { AppStoreConfig, CellData } from '@ddt-web/data-service';
import { DialogRef } from '@ddt-web/dialog';
import { WebUserDataConstants } from '@ddt-web/domain';
import { ImagePathUtil, Memoize } from '@ddt-web/generic-utils';
import { StoreService } from '@ddt-web/store-service';
import * as get from 'lodash/get';
import { Observable } from 'rxjs';
import { concatAll, filter, map, toArray } from 'rxjs/operators';

import { MDSScenarioCategory } from '../models/scenarios.interface';

@Injectable({
	providedIn: 'root'
})
export class ScenarioService {

	constructor(
		private http: HttpClient,
		private servicePathProvider: ServicePathProviderService,
		private storeService: StoreService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private domSanitizer: DomSanitizer
	) { }

	@Memoize()
	public getImagePath(imageName: string = ''): SafeUrl {
		if (!imageName) {
			return null;
		}

		if (!imageName.endsWith('.svg')) {
			imageName = imageName + '.svg';
		}

		return this.domSanitizer.bypassSecurityTrustUrl(ImagePathUtil.getImagePath('scenarios/' + imageName));
	}

	/**
	 * Fetches the Market Driven Scenarios config and returns the observable of the mdsGroups.
	 * If there is no resultMap.REPORT[0].data.mdsGroups, an observable of empty array is returned.
	 */
	public fetchMdsScenarioConfig(): Observable<any> {
		const apiGatewayPath = this.servicePathProvider.getServicePath('api-gateway');

		return this.http.get(apiGatewayPath + WebUserDataConstants.MDS_SCENARIOS_CONFIG)
			.pipe(
				map(jsonResponse => get(jsonResponse, 'resultMap.REPORT[0].data.mdsGroups', [])),
				concatAll(),
				filter(mdsGroup => get(mdsGroup, 'isEnabled', true) === true),
				toArray()
			);
	}

	/**
	 * Returns the mds scenarios from the mdsScenarios feature store.
	 *
	 * @param {AppStoreConfig} storeConfig store config with mdsScenarios feature store
	 * @returns {Array<MDSScenarioCategory>} mdsScenarios
	 */
	public getMdsScenariosFromStore(storeConfig: AppStoreConfig): MDSScenarioCategory[] {
		const mdsScenarios: MDSScenarioCategory[] = [];

		// Revisit this implementation. This is async subscription inside a synchronous request. The return statement below could be empty!
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.storeService.selectAll(storeConfig.mdsScenarios).subscribe((mdsScenariosConfig: any) => {
			if (!mdsScenariosConfig.isLoading && mdsScenariosConfig.entities) {
				const entities: MDSScenarioCategory[] = Object.values(mdsScenariosConfig.entities as MDSScenarioCategory);
				mdsScenarios.push(...entities);
			}
		});

		return mdsScenarios;
	}

	public assignColumnClasses(columns: Array<CellData>): { scenarioNameColumnClass: string; scenarioValueColumnClass: string; scenarioChartColumnClass: string } {
		let scenarioNameColumnClass: string;
		let scenarioValueColumnClass: string;
		let scenarioChartColumnClass: string;
		switch (columns.length) {
			case 1:
				scenarioNameColumnClass = 'col-14';
				scenarioValueColumnClass = 'col-10';
				scenarioChartColumnClass = 'col-10';
				break;
			case 2:
				scenarioNameColumnClass = 'col-10';
				scenarioValueColumnClass = 'col-7';
				scenarioChartColumnClass = 'col-14';
				break;
			case 3:
				scenarioNameColumnClass = 'col-9';
				scenarioValueColumnClass = 'col-5';
				scenarioChartColumnClass = 'col-15';
				break;
			case 4:
				scenarioNameColumnClass = 'col-8';
				scenarioValueColumnClass = 'col-4';
				scenarioChartColumnClass = 'col-16';
				break;
			case 5:
				scenarioNameColumnClass = 'col-9';
				scenarioValueColumnClass = 'col-3';
				scenarioChartColumnClass = 'col-15';
				break;
			case 6:
				scenarioNameColumnClass = 'col-6';
				scenarioValueColumnClass = 'col-3';
				scenarioChartColumnClass = 'col-18';
				break;
			case 7:
				scenarioNameColumnClass = 'col-10';
				scenarioValueColumnClass = 'col-2';
				scenarioChartColumnClass = 'col-14';
				break;
		}
		return { scenarioNameColumnClass: scenarioNameColumnClass, scenarioValueColumnClass: scenarioValueColumnClass, scenarioChartColumnClass: scenarioChartColumnClass };
	}

	public convertKeysToSelectedScenarios(selectedScenarioKeys: Array<string>, defaultScenarioKeys: Array<string>, allScenarios: Array<CellData>): Array<CellData> {
		if (selectedScenarioKeys && selectedScenarioKeys.length) {
			return selectedScenarioKeys.map(key => allScenarios.find(scenario => scenario.rawValue.toString() === key));
		} else {
			// if there are no user selected scenarios, then use the default ones
			return defaultScenarioKeys.map(key => allScenarios.find(scenario => scenario.rawValue.toString() === key));
		}
	}

	public saveScenarioSelection(dialogRef: DialogRef, selectedScenarioKeys: Array<string>): void {
		this.sendTealiumEventForSaveButton(selectedScenarioKeys);
		dialogRef.close(selectedScenarioKeys);
	}


	private sendTealiumEventForSaveButton(selectedScenarioKeys: Array<string>): void {
		const tealiumEventName = `scenarios:scenario-selection-dialog:button:save:${selectedScenarioKeys}`;
		const tealiumEvent = new TealiumAnalyticsEvent(tealiumEventName, {});
		this.tealiumAnalyticsService.trackEventTealium(tealiumEvent);
	}
}
